import React, {useState} from 'react';

import {shuffle} from 'lodash';
import CSSModules from 'react-css-modules';

import type {OnSelectAnswer, OnUnselectAnswer} from './Answer';
import Answer from './Answer';
import QuestionCountHeader from './QuestionCountHeader';

import styles from './styles.module.scss';

type Answers = {
  text: string;
  solution: boolean;
  isSelected: boolean;
  tip?: string;
};

type CommonProps = {
  answers: Answers[];
  numberOfQuestions: number;
  questionNumberText?: string;
  multipleChoice: boolean;
  shuffleAnswers?: boolean;
  disabled?: boolean;
  isAssessment?: boolean;
  reskinClassName?: string;
  isPreview?: boolean;
  accessibilityLabels: {
    icon: {
      correct: string;
      incorrect: string;
    };
  };
};

type ConditionalEditModeProps =
  | {
      editMode?: false;
      questionIndex: number;
      onSelectAnswer: (
        qIndex: number,
        questionAlreadyAnswered: boolean
      ) => OnSelectAnswer;
      onUnselectAnswer: (qIndex: number) => OnUnselectAnswer;
    }
  | {
      editMode?: true;
      questionIndex?: never;
      onSelectAnswer?: never;
      onUnselectAnswer?: never;
    };

type QuestionProps = CommonProps & ConditionalEditModeProps;

const Question = ({
  editMode,
  answers,
  questionIndex,
  numberOfQuestions,
  multipleChoice,
  questionNumberText,
  shuffleAnswers = true,
  disabled,
  onSelectAnswer,
  onUnselectAnswer,
  isAssessment = false,
  reskinClassName,
  isPreview = false,
  accessibilityLabels,
}: QuestionProps): JSX.Element => {
  const [answersOrder] = useState(() =>
    shuffle([...Array(answers.length).keys()])
  );

  const answersWithIndex = answers.map((answer, index) => ({
    answer,
    index,
  }));
  const shuffledAnswers = !shuffleAnswers
    ? answersWithIndex
    : answersWithIndex.sort((a, b) =>
        answersOrder.indexOf(a.index) < answersOrder.indexOf(b.index) ? -1 : 1
      );

  const questionAlreadyAnswered = answers
    .filter((answer) => answer.solution)
    .every((answer) => answer.isSelected);

  return (
    <div
      styleName={`question${editMode ? ' mobile-style' : ''} ${
        isPreview ? 'preview-mode' : ''
      } ${isAssessment ? 'assessment' : ''}`}
    >
      <QuestionCountHeader
        numberOfQuestions={numberOfQuestions}
        questionNumberText={questionNumberText}
      />
      {shuffledAnswers.map(({answer, index}) => (
        <Answer
          isPreview={isPreview}
          key={index}
          answerIndex={index}
          answer={answer}
          multipleChoice={multipleChoice}
          isAssessment={isAssessment}
          onSelectAnswer={
            onSelectAnswer &&
            onSelectAnswer(questionIndex, questionAlreadyAnswered)
          }
          onUnselectAnswer={onUnselectAnswer && onUnselectAnswer(questionIndex)}
          disabled={disabled || editMode}
          reskinClassName={reskinClassName}
          accessibilityLabels={accessibilityLabels}
        />
      ))}
    </div>
  );
};

export default CSSModules(Question, styles, {allowMultiple: true});
